import React from "react";
import DataTable from "react-data-table-component";
import { ChevronLeft, ChevronRight } from "lucide-react";

const paginationStyles = {
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    padding: "16px 0",
    gap: "8px",
    flexWrap: "wrap"
  },
  link: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "6px 8px"
  },
  linkDisabled: {
    color: "#9ca3af",
    cursor: "not-allowed",
    opacity: 0.7
  },
  pageNum: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    minWidth: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    border: "none",
    backgroundColor: "transparent",
    borderRadius: "6px"
  },
  pageNumActive: {
    backgroundColor: "#e5e7eb",
    color: "#374151",
    fontWeight: 500
  },
  ellipsis: {
    color: "#6b7280",
    fontSize: "0.875rem",
    padding: "0 4px",
    minWidth: "24px",
    textAlign: "center"
  }
};

function CustomPagination({ rowCount, rowsPerPage, currentPage, onChangePage, totalRows }) {
  const total = rowCount ?? totalRows ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i);
      if (totalPages > 5) pages.push("...");
      if (totalPages > 5) pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      if (totalPages > 5) pages.push("...");
      for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div style={paginationStyles.wrapper}>
      <button
        type="button"
        aria-label="Previous page"
        style={{
          ...paginationStyles.link,
          ...(!canPrev ? paginationStyles.linkDisabled : {})
        }}
        onClick={() => canPrev && onChangePage(currentPage - 1)}
        disabled={!canPrev}
      >
        <ChevronLeft size={18} />
        Previous
      </button>
      {pageNumbers.map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} style={paginationStyles.ellipsis}>
            ...
          </span>
        ) : (
          <button
            key={p}
            type="button"
            style={{
              ...paginationStyles.pageNum,
              ...(p === currentPage ? paginationStyles.pageNumActive : {})
            }}
            onClick={() => onChangePage(p)}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        aria-label="Next page"
        style={{
          ...paginationStyles.link,
          ...(!canNext ? paginationStyles.linkDisabled : {})
        }}
        onClick={() => canNext && onChangePage(currentPage + 1)}
        disabled={!canNext}
      >
        Next
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

const OrdersTable = ({ ordersData, onViewOrder }) => {
  const ordersColumns = [
    {
      name: "Date Issued",
      selector: (row) => row.dateIssued,
      sortable: true
    },
    {
      name: "Order ID",
      selector: (row) => row.orderId,
      sortable: true
    },
    {
      name: "Merchant Name",
      selector: (row) => row.merchantName,
      sortable: true
    },
    {
      name: "Order Type",
      selector: (row) => row.orderType,
      sortable: true
    },
    {
      name: "Facilities",
      selector: (row) => row.facilities,
      sortable: true
    },
    {
      name: "Order Status",
      selector: (row) => row.orderStatus,
      sortable: true
    },
    {
      name: "Delivery ETA",
      selector: (row) => row.deliveryETA,
      sortable: true
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          type="button"
          className="orders-view-link"
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: "#1E56A0",
            cursor: "pointer",
            fontSize: "0.875rem"
          }}
          onClick={(e) => {
            e.stopPropagation();
            onViewOrder?.(row);
          }}
        >
          View Order
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#002347",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px"
      }
    },
    pagination: {
      style: {
        justifyContent: "center",
        flexWrap: "wrap"
      }
    }
  };

  return (
    <div className="orders-page">
      <div className="orders-header">
        <h1 className="orders-title">Orders</h1>
      </div>

      <div className="orders-table-section">
        <div className="table-container">
          <DataTable
            columns={ordersColumns}
            data={ordersData}
            customStyles={customStyles}
            striped
            highlightOnHover
            noHeader={false}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10]}
            paginationComponent={CustomPagination}
          />
        </div>
      </div>
    </div>
  );
};

export default OrdersTable;

